"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import { ChevronUp, ChevronDown, Check, X, RotateCcw, Play, Share2, Trophy } from "lucide-react"

interface QuizItem {
  name: string
  value: number
  slug: string | null
}

interface QuizGameProps {
  items: QuizItem[]
  typeName: string
  valueLabel: string
  valueUnit: string | null
  valuePrefix: boolean
  categorySlug: string
  typeSlug: string
}

function formatVal(value: number, unit: string | null, prefix: boolean): string {
  let formatted: string
  if (value >= 1_000_000_000) formatted = (value / 1_000_000_000).toFixed(1) + "B"
  else if (value >= 1_000_000) formatted = (value / 1_000_000).toFixed(1) + "M"
  else if (value >= 1_000) formatted = (value / 1_000).toFixed(1) + "K"
  else formatted = value.toLocaleString()
  if (unit) return prefix ? `${unit}${formatted}` : `${formatted} ${unit}`
  return formatted
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function generatePairs(items: QuizItem[]): { a: QuizItem; b: QuizItem }[] {
  const shuffled = shuffleArray(items)
  const pairs: { a: QuizItem; b: QuizItem }[] = []
  for (let i = 0; i < Math.min(shuffled.length - 1, 20); i += 2) {
    pairs.push({ a: shuffled[i], b: shuffled[i + 1] })
  }
  return pairs.slice(0, 10)
}

export function QuizGame({ items, typeName, valueLabel, valueUnit, valuePrefix, categorySlug, typeSlug }: QuizGameProps) {
  const [pairs, setPairs] = useState(() => generatePairs(items))
  const [current, setCurrent] = useState(0)
  const [score, setScore] = useState(0)
  const [answers, setAnswers] = useState<(boolean | null)[]>([])
  const [revealed, setRevealed] = useState(false)
  const [lastCorrect, setLastCorrect] = useState<boolean | null>(null)

  const pair = pairs[current]
  const finished = current >= pairs.length || !pair

  const scoreLabel = useMemo(() => {
    if (!finished) return ""
    const pct = (score / pairs.length) * 100
    if (pct === 100) return "Perfect!"
    if (pct >= 80) return "Excellent!"
    if (pct >= 60) return "Good job!"
    if (pct >= 40) return "Not bad!"
    return "Keep practicing!"
  }, [finished, score, pairs.length])

  const handleGuess = (guess: "higher" | "lower") => {
    if (revealed || !pair) return
    const isCorrect = guess === "higher" ? pair.b.value >= pair.a.value : pair.b.value <= pair.a.value
    setRevealed(true)
    setLastCorrect(isCorrect)
    if (isCorrect) setScore((s) => s + 1)
    setAnswers((a) => [...a, isCorrect])
  }

  const next = () => {
    setRevealed(false)
    setLastCorrect(null)
    setCurrent((c) => c + 1)
  }

  const restart = () => {
    setPairs(generatePairs(items))
    setCurrent(0)
    setScore(0)
    setAnswers([])
    setRevealed(false)
    setLastCorrect(null)
  }

  const shareText = `I scored ${score}/${pairs.length} on the ${typeName} Quiz! 🎯\n\nCan you beat my score?\nhttps://playhigherlower.com/quiz/${categorySlug}/${typeSlug}`

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText })
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(shareText)
    }
  }

  if (finished) {
    return (
      <div className="space-y-6">
        <div className="p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
          <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <div className="text-5xl font-black text-white mb-2">{score}/{pairs.length}</div>
          <div className="text-xl text-zinc-400 mb-6">{scoreLabel}</div>

          {/* Answer recap */}
          <div className="flex justify-center gap-1.5 mb-8">
            {answers.map((correct, i) => (
              <div key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center ${correct ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                {correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 max-w-xs mx-auto">
            <button onClick={handleShare} className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors">
              <Share2 className="w-5 h-5" />
              Share result
            </button>
            <button onClick={restart} className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-white text-zinc-900 hover:bg-zinc-100 font-semibold transition-colors">
              <RotateCcw className="w-5 h-5" />
              Try again
            </button>
            <Link href={`/play/${categorySlug}/${typeSlug}`} className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-semibold hover:bg-zinc-700 transition-colors">
              <Play className="w-5 h-5" />
              Play the full game
            </Link>
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800">
          <h3 className="text-lg font-semibold text-white mb-4">Keep exploring</h3>
          <div className="flex flex-wrap gap-3">
            <Link href={`/top-lists/${categorySlug}/${typeSlug}`} className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:border-zinc-500 transition-all">View top list</Link>
            <Link href="/tools/random-trivia-generator" className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:border-zinc-500 transition-all">Random trivia</Link>
            <Link href={`/tiers/${categorySlug}/${typeSlug}`} className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:border-zinc-500 transition-all">View tiers</Link>
            <Link href="/quiz" className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:border-zinc-500 transition-all">More quizzes</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-zinc-400">Question {current + 1} of {pairs.length}</span>
        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${((current + (revealed ? 1 : 0)) / pairs.length) * 100}%` }} />
        </div>
        <span className="text-sm font-bold text-white">{score}</span>
      </div>

      {/* Question card */}
      <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
        <div className="text-center mb-6">
          <p className="text-zinc-400 mb-2">Which has a higher {valueLabel.toLowerCase()}?</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {/* Item A */}
          <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700 text-center">
            <div className="text-lg font-bold text-white mb-2">{pair.a.name}</div>
            <div className="text-2xl font-black text-emerald-400">
              {formatVal(pair.a.value, valueUnit, valuePrefix)}
            </div>
          </div>

          {/* Item B */}
          <div className={`p-5 rounded-xl border text-center transition-all ${
            revealed
              ? lastCorrect ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"
              : "bg-zinc-800/50 border-zinc-700"
          }`}>
            <div className="text-lg font-bold text-white mb-2">{pair.b.name}</div>
            {revealed ? (
              <div className={`text-2xl font-black ${lastCorrect ? "text-emerald-400" : "text-rose-400"}`}>
                {formatVal(pair.b.value, valueUnit, valuePrefix)}
              </div>
            ) : (
              <div className="text-2xl font-black text-zinc-500">???</div>
            )}
          </div>
        </div>

        {!revealed ? (
          <div className="flex justify-center gap-3">
            <button
              onClick={() => handleGuess("higher")}
              className="h-12 px-6 text-sm font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/25 inline-flex items-center gap-2 transition-colors"
            >
              <ChevronUp className="h-5 w-5" />
              Higher
            </button>
            <button
              onClick={() => handleGuess("lower")}
              className="h-12 px-6 text-sm font-bold bg-rose-500 hover:bg-rose-600 text-white rounded-xl shadow-lg shadow-rose-500/25 inline-flex items-center gap-2 transition-colors"
            >
              <ChevronDown className="h-5 w-5" />
              Lower
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${lastCorrect ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
              {lastCorrect ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              <span className="text-sm font-semibold">{lastCorrect ? "Correct!" : "Wrong!"}</span>
            </div>
            <br />
            <button onClick={next} className="mt-2 px-6 py-3 rounded-xl bg-white text-zinc-900 font-semibold hover:bg-zinc-100 transition-colors">
              Next question
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
